import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

import { Badge } from '~/components/ui/badge';
import { cn } from '~/utils/cn';

import { type AvailabilityTone, getAvailability } from '../utils/product-detail';

/** Outline badge tinted with the design-system semantic tokens per tone. */
const TONE_CLASSES: Record<AvailabilityTone, string> = {
  success: 'border-success/30 bg-success-50 text-success',
  warning: 'border-warning/30 bg-warning-50 text-warning',
  info: 'border-info/30 bg-info-50 text-info',
  neutral: 'border-border bg-muted text-muted-foreground',
};

/** Soft-filled pill background per tone (the large detail-header variant). */
const TONE_PILL: Record<AvailabilityTone, string> = {
  success: 'bg-success-50',
  warning: 'bg-warning-50',
  info: 'bg-info-50',
  neutral: 'bg-muted',
};

/** Leading icon + tint per tone for the large variant. */
const TONE_ICON: Record<AvailabilityTone, { Icon: typeof CheckCircle2; color: string }> = {
  success: { Icon: CheckCircle2, color: 'text-success' },
  warning: { Icon: AlertTriangle, color: 'text-warning' },
  info: { Icon: Info, color: 'text-info' },
  neutral: { Icon: Info, color: 'text-muted-foreground' },
};

/** Availability pill — humanises the raw feed enum and colours it by semantic
 *  tone (in stock → success, out of stock → warning, …). Renders nothing when
 *  availability is absent, so callers can drop it in unconditionally.
 *
 *  `size="lg"` renders the detail-header treatment: a soft-filled rounded pill
 *  with a leading status icon. Default `"sm"` is the compact table/inline badge. */
export function AvailabilityBadge({
  availability,
  className,
  size = 'sm',
}: {
  availability: string | null | undefined;
  className?: string;
  size?: 'sm' | 'lg';
}) {
  const { label, tone } = getAvailability(availability);
  if (!label) return null;

  if (size === 'lg') {
    const { Icon, color } = TONE_ICON[tone];
    return (
      <span
        className={cn(
          'text-foreground inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium',
          TONE_PILL[tone],
          className,
        )}
      >
        <Icon aria-hidden="true" className={cn('h-4 w-4 shrink-0', color)} />
        {label}
      </span>
    );
  }

  return (
    <Badge variant="outline" className={cn('font-medium', TONE_CLASSES[tone], className)}>
      {label}
    </Badge>
  );
}
